import constant from "../const";
import dateUtil from "@/utils/dateUtil";
import consoleController from "../utils/logUtil";
import i18n from "@/i18n";

/**
 * 肌分析メッセージ出し分けアルゴリズム
 * @param result 肌分析結果
 * @param skinAnalysisDataList 過去の肌分析結果リスト
 * @return 肌分析メッセージ
 */

function getSkinAnalysisMessage(result, skinAnalysisDataList) {
  // 優先度が高い順に記載する
  const features = [
    "firmness",
    "moisture",
    "ageSpots",
    "texture",
    "pore",
    "wrinkles",
    "darkCirclesV2",
  ];
  if (skinAnalysisDataList && skinAnalysisDataList.length !== 0) {
    // 過去の最高得点を取得
    const bestScoresOfFeatures = [0, 0, 0, 0, 0, 0, 0];
    for (const skinAnalysisData of skinAnalysisDataList) {
      for (let i = 0; i < features.length; i++) {
        if (skinAnalysisData[features[i]] > bestScoresOfFeatures[i]) {
          bestScoresOfFeatures[i] = skinAnalysisData[features[i]];
        }
      }
    }

    // 過去最高得点を更新した項目を格納
    const bestFeatures = [];
    for (let i = 0; i < features.length; i++) {
      if (result[features[i]] > bestScoresOfFeatures[i]) {
        bestFeatures.push(features[i]);
      }
    }
    consoleController(
      "log",
      `bestScoresOfFeatures: ${JSON.stringify(bestScoresOfFeatures, null, 4)}`
    );
    consoleController(
      "log",
      `bestFeatures: ${JSON.stringify(bestFeatures, null, 4)}`
    );

    // 過去最高点が出たとき
    if (bestFeatures.length >= 3) {
      return i18n.global.t("skinAnalysis.messages.best3more");
    }
    if (bestFeatures.length === 2) {
      const feature1 = i18n.global.t(
        constant.SKIN_ANALYSIS_FEATURES_DICT[bestFeatures[0]]
      );
      const feature2 = i18n.global.t(
        constant.SKIN_ANALYSIS_FEATURES_DICT[bestFeatures[1]]
      );
      return i18n.global.t("skinAnalysis.messages.best2", {
        feature1,
        feature2,
      });
    }
    if (bestFeatures.length === 1) {
      const feature = i18n.global.t(
        constant.SKIN_ANALYSIS_FEATURES_DICT[bestFeatures[0]]
      );
      return i18n.global.t("skinAnalysis.messages.best1", { feature });
    }

    // 前回値との比較
    skinAnalysisDataList.sort((a, b) =>
      dateUtil.sortISOStringDescending(a.dateTime, b.dateTime)
    );
    const previousData = skinAnalysisDataList[0];
    // 前回値よりも点数が高い項目を格納する
    const betterFeatures = [];
    for (const feature of features) {
      if (result[feature] > previousData[feature]) {
        betterFeatures.push(feature);
      }
    }

    consoleController(
      "log",
      `previousData: ${JSON.stringify(previousData, null, 4)}`
    );
    consoleController(
      "log",
      `betterFeatures: ${JSON.stringify(betterFeatures, null, 4)}`
    );

    if (betterFeatures.length >= 4) {
      return i18n.global.t("skinAnalysis.messages.better4more");
    }
    if (betterFeatures.length === 3) {
      return i18n.global.t("skinAnalysis.messages.better3");
    }
    if (betterFeatures.length === 2) {
      const feature1 = i18n.global.t(
        constant.SKIN_ANALYSIS_FEATURES_DICT[betterFeatures[0]]
      );
      const feature2 = i18n.global.t(
        constant.SKIN_ANALYSIS_FEATURES_DICT[betterFeatures[1]]
      );
      return i18n.global.t("skinAnalysis.messages.better2", {
        feature1,
        feature2,
      });
    }
    if (betterFeatures.length === 1) {
      const feature = i18n.global.t(
        constant.SKIN_ANALYSIS_FEATURES_DICT[betterFeatures[0]]
      );
      return i18n.global.t("skinAnalysis.messages.better1", { feature });
    }
  }

  let bestScore = 0;
  let worstScore = 101;
  let bestFeature = "";
  let worstFeature = "";

  for (const feature of features) {
    if (result[feature] > bestScore) {
      bestScore = result[feature];
      bestFeature = feature;
    }
    if (result[feature] < worstScore) {
      worstScore = result[feature];
      worstFeature = feature;
    }
  }

  if (bestScore >= 80 && worstScore < 80) {
    const feature1 = i18n.global.t(
      constant.SKIN_ANALYSIS_FEATURES_DICT[bestFeature]
    );
    const feature2 = i18n.global.t(
      constant.SKIN_ANALYSIS_FEATURES_DICT[worstFeature]
    );
    return i18n.global.t("skinAnalysis.messages.highAndConcern", {
      feature1,
      feature2,
    });
  }
  if (bestScore < 80 && bestScore >= 70) {
    const feature1 = i18n.global.t(
      constant.SKIN_ANALYSIS_FEATURES_DICT[bestFeature]
    );
    const feature2 = i18n.global.t(
      constant.SKIN_ANALYSIS_FEATURES_DICT[worstFeature]
    );
    return i18n.global.t("skinAnalysis.messages.goodAndConcern", {
      feature1,
      feature2,
    });
  }
  if (worstScore >= 80) {
    const feature = i18n.global.t(
      constant.SKIN_ANALYSIS_FEATURES_DICT[bestFeature]
    );
    return i18n.global.t("skinAnalysis.messages.high", { feature });
  }
  const feature = i18n.global.t(
    constant.SKIN_ANALYSIS_FEATURES_DICT[worstFeature]
  );
  return i18n.global.t("skinAnalysis.messages.concern", { feature });
}

export default {
  getSkinAnalysisMessage,
};
